"use client";
import React, { forwardRef, useState, useMemo } from "react";
import { Glass } from "../../../primitives";
import { Motion } from "../../../primitives";
import { GlassSearchInterface } from "../../../components/interactive/GlassSearchInterface";
import { GlassDataTable } from "../../../components/data-display/GlassDataTable";
import {
  GlassButton,
  IconButton,
} from "../../../components/button/GlassButton";
import { GlassBadge } from "../../../components/data-display/GlassBadge";
import { PageHeader } from "../../../components/layout/GlassAppShell";
import { VStack, HStack } from "../../../components/layout/GlassStack";
import { GlassGrid, GlassGridItem } from "../../../components/layout/GlassGrid";
import { GlassCard } from "../../../components/card/GlassCard";
import { GlassInput } from "../../../components/input/GlassInput";
import { cn } from "@/lib/utils";

export type ListCellValue = unknown;
export type ListComparableValue = string | number | Date;
export type ListFilterRangeValue = readonly [
  ListComparableValue,
  ListComparableValue,
];
export type ListFilterMultiValue = readonly ListComparableValue[];
export type ListFilterValue =
  | ListComparableValue
  | boolean
  | null
  | undefined
  | ListFilterRangeValue
  | ListFilterMultiValue;
export type ListFiltersState = Record<string, ListFilterValue>;
export type ListViewMode = "table" | "grid" | "list";
export type ListItem = Record<string, ListCellValue> & {
  id: string;
};

export interface ListColumn {
  id: string;
  header: string;
  accessorKey?: string;
  accessorFn?: (row: ListItem) => ListCellValue;
  cell?: (props: { row: ListItem; value: ListCellValue }) => React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  width?: string | number;
  align?: "left" | "center" | "right";
}

export interface ListFilter {
  id: string;
  label: string;
  type: "select" | "multiselect" | "range" | "date" | "text";
  options?: Array<{ value: string; label: string; count?: number }>;
  value?: ListFilterValue;
  placeholder?: string;
}

export interface ListSort {
  column: string;
  direction: "asc" | "desc";
}

export interface ListAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  variant?: "default" | "primary" | "secondary" | "error";
  onClick: (items: ListItem[]) => void;
  requiresSelection?: boolean;
  single?: boolean; // Only for single item selection
}

export interface GlassListViewProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Page title
   */
  title?: string;
  /**
   * Page description
   */
  description?: string;
  /**
   * List data
   */
  data?: ListItem[];
  /**
   * Table columns
   */
  columns?: ListColumn[];
  /**
   * Available filters
   */
  filters?: ListFilter[];
  /**
   * Active filters
   */
  activeFilters?: ListFiltersState;
  /**
   * Filter change handler
   */
  onFiltersChange?: (filters: ListFiltersState) => void;
  /**
   * Search query
   */
  searchQuery?: string;
  /**
   * Search change handler
   */
  onSearchChange?: (query: string) => void;
  /**
   * Sort configuration
   */
  sort?: ListSort;
  /**
   * Sort change handler
   */
  onSortChange?: (sort: ListSort) => void;
  /**
   * Current page
   */
  currentPage?: number;
  /**
   * Page size
   */
  pageSize?: number;
  /**
   * Total items count
   */
  totalItems?: number;
  /**
   * Pagination change handler
   */
  onPaginationChange?: (page: number, pageSize: number) => void;
  /**
   * View mode
   */
  viewMode?: ListViewMode;
  /**
   * View mode change handler
   */
  onViewModeChange?: (mode: ListViewMode) => void;
  /**
   * Available actions
   */
  actions?: ListAction[];
  /**
   * Primary action (for create/add)
   */
  primaryAction?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  };
  /**
   * Selected items
   */
  selectedItems?: string[];
  /**
   * Selection change handler
   */
  onSelectionChange?: (selectedIds: string[]) => void;
  /**
   * Row click handler
   */
  onRowClick?: (item: ListItem) => void;
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Empty state
   */
  emptyState?: React.ReactNode;
  /**
   * Card renderer for grid/list view
   */
  renderCard?: (item: ListItem, index: number) => React.ReactNode;
  /**
   * Bulk selection enabled
   */
  selectable?: boolean;
}

const VIEW_MODES: Array<{ key: ListViewMode; icon: string; label: string }> = [
  { key: "table", icon: "📊", label: "Table" },
  { key: "grid", icon: "▦", label: "Grid" },
  { key: "list", icon: "☰", label: "List" },
];

const isActiveFilterValue = (
  value: ListFilterValue
): value is Exclude<ListFilterValue, null | undefined | ""> => {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  return !Array.isArray(value) || value.length > 0;
};

const toSearchableText = (value: ListCellValue): string => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
};

const toDisplayText = (value: ListCellValue, fallback = ""): string => {
  if (typeof value === "string") {
    return value || fallback;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return fallback;
};

const toFilterControlValue = (
  value: ListFilterValue
): string | number | readonly string[] => {
  if (value === null || value === undefined) {
    return "";
  }

  if (Array.isArray(value)) {
    return value.map((item) =>
      item instanceof Date ? item.toISOString() : String(item)
    );
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "boolean") {
    return String(value);
  }

  return value as string | number;
};

const isComparableFilterValue = (
  value: ListFilterValue
): value is ListComparableValue =>
  typeof value === "string" ||
  typeof value === "number" ||
  value instanceof Date;

const toComparableNumber = (value: ListCellValue): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const isRangeFilterValue = (
  value: ListFilterValue
): value is ListFilterRangeValue =>
  Array.isArray(value) &&
  value.length === 2 &&
  isComparableFilterValue(value[0]) &&
  isComparableFilterValue(value[1]);

const matchesRangeFilter = (
  itemValue: ListCellValue,
  filterValue: ListFilterRangeValue
) => {
  const numericValue = toComparableNumber(itemValue);
  const min = toComparableNumber(filterValue[0]);
  const max = toComparableNumber(filterValue[1]);

  if (numericValue === null || min === null || max === null) {
    return true;
  }

  return numericValue >= min && numericValue <= max;
};

/**
 * GlassListView component
 * Comprehensive list view with filtering, search, and multiple view modes
 */
export const GlassListView = forwardRef<HTMLDivElement, GlassListViewProps>(
  (
    {
      title = "List View",
      description,
      data = [],
      columns = [],
      filters = [],
      activeFilters = {},
      onFiltersChange,
      searchQuery = "",
      onSearchChange,
      sort,
      onSortChange,
      currentPage = 1,
      pageSize = 20,
      totalItems,
      onPaginationChange,
      viewMode = "table",
      onViewModeChange,
      actions = [],
      primaryAction,
      selectedItems = [],
      onSelectionChange,
      onRowClick,
      loading = false,
      emptyState,
      renderCard,
      selectable = true,
      className,
      "aria-label": ariaLabel,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    const [showFilters, setShowFilters] = useState(false);

    // Filter and search data locally if no external handlers
    const filteredData = useMemo(() => {
      let result = [...data];

      // Apply search
      if (searchQuery && !onSearchChange) {
        result = result.filter((item) =>
          columns.some((column) => {
            const value = column.accessorFn
              ? column.accessorFn(item)
              : column.accessorKey
                ? item?.[column.accessorKey]
                : "";
            return toSearchableText(value)
              .toLowerCase()
              .includes(searchQuery.toLowerCase());
          })
        );
      }

      // Apply filters
      if (Object.keys(activeFilters).length > 0 && !onFiltersChange) {
        result = result.filter((item) => {
          return Object.entries(activeFilters).every(
            ([filterId, filterValue]) => {
              const filter = filters.find((f) => f.id === filterId);
              if (!filter || !isActiveFilterValue(filterValue)) return true;

              const itemValue = item?.[filterId];

              switch (filter.type) {
                case "select":
                  return itemValue === filterValue;
                case "multiselect":
                  return Array.isArray(filterValue)
                    ? filterValue.includes(itemValue)
                    : true;
                case "text":
                  return String(itemValue)
                    .toLowerCase()
                    .includes(String(filterValue).toLowerCase());
                case "range":
                  if (isRangeFilterValue(filterValue)) {
                    return matchesRangeFilter(itemValue, filterValue);
                  }
                  return true;
                default:
                  return true;
              }
            }
          );
        });
      }

      return result;
    }, [
      data,
      searchQuery,
      activeFilters,
      filters,
      columns,
      onSearchChange,
      onFiltersChange,
    ]);

    // Calculate pagination
    const totalFilteredItems = totalItems || filteredData?.length || 0;
    const totalPages = Math.ceil(totalFilteredItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageData = onPaginationChange
      ? filteredData
      : filteredData.slice(startIndex, endIndex);

    // Handle selection
    const handleSelectionChange = (newSelection: string[]) => {
      onSelectionChange?.(newSelection);
    };

    // Handle bulk actions
    const handleBulkAction = (action: ListAction) => {
      const selectedData = data?.filter((item) =>
        selectedItems.includes(item?.id)
      );
      action.onClick(selectedData);
    };

    // Render view mode toggle
    const renderViewModeToggle = () => {
      if (!onViewModeChange) return null;

      return (
        <HStack data-glass-component space="none">
          {VIEW_MODES.map((mode) => (
            <IconButton
              key={mode.key}
              icon={mode.icon}
              variant={viewMode === mode.key ? "primary" : "ghost"}
              size="sm"
              onClick={() => onViewModeChange(mode.key)}
              aria-label={mode.label}
            />
          ))}
        </HStack>
      );
    };

    // Render filters
    const renderFilters = () => {
      if (!showFilters || (filters?.length || 0) === 0) return null;

      return (
        <Motion preset="slideDown">
          <Glass className="glass-p-4 glass-radius-lg">
            <VStack space="md">
              <HStack space="sm" align="center" justify="between">
                <h3 className="glass-text-sm glass-font-medium glass-text-primary">
                  Filters
                </h3>
                <GlassButton
                  variant="ghost"
                  size="sm"
                  onClick={(e) => onFiltersChange?.({})}
                  disabled={Object.keys(activeFilters).length === 0}
                >
                  Clear All
                </GlassButton>
              </HStack>

              <div className="glass-grid glass-grid-cols-1 md:glass-grid-cols-2 lg:glass-grid-cols-3 xl:glass-grid-cols-4 glass-gap-4">
                {filters.map((filter) => (
                  <div key={filter.id}>
                    <label className="glass-block glass-text-sm glass-font-medium glass-text-primary glass-mb-2">
                      {filter.label}
                    </label>

                    {filter.type === "select" && (
                      <select
                        value={toFilterControlValue(activeFilters?.[filter.id])}
                        onChange={(e) =>
                          onFiltersChange?.({
                            ...activeFilters,
                            [filter.id]: e.target.value,
                          })
                        }
                        className="glass-w-full glass-px-3 glass-py-2 glass-bg-background glass-border glass-border-glass-border glass-radius-md glass-focus-outline-none glass-focus-ring-2 glass-focus-ring-primary"
                      >
                        <option value="">
                          {filter.placeholder || "Select..."}
                        </option>
                        {filter.options?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label} {option.count && `(${option.count})`}
                          </option>
                        ))}
                      </select>
                    )}

                    {filter.type === "text" && (
                      <GlassInput
                        type="text"
                        value={toFilterControlValue(activeFilters?.[filter.id])}
                        onChange={(e) =>
                          onFiltersChange?.({
                            ...activeFilters,
                            [filter.id]: e.target.value,
                          })
                        }
                        placeholder={filter.placeholder}
                        className="glass-w-full glass-px-3 glass-py-2 glass-bg-background glass-border glass-border-glass-border glass-radius-md glass-focus-outline-none glass-focus-ring-2 glass-focus-ring-primary"
                      />
                    )}
                  </div>
                ))}
              </div>
            </VStack>
          </Glass>
        </Motion>
      );
    };

    // Render actions bar
    const renderActionsBar = () => {
      const hasSelectedItems = (selectedItems?.length || 0) > 0;
      const availableActions = actions.filter(
        (action) => !action.requiresSelection || hasSelectedItems
      );

      if ((availableActions?.length || 0) === 0 && !primaryAction) return null;

      return (
        <HStack space="sm" align="center" justify="between">
          <HStack space="sm" align="center">
            {hasSelectedItems && (
              <span className="glass-text-sm glass-text-secondary">
                {selectedItems?.length || 0} item
                {(selectedItems?.length || 0) !== 1 ? "s" : ""} selected
              </span>
            )}

            {availableActions.map((action) => (
              <GlassButton
                key={action.id}
                variant={action.variant || "outline"}
                size="sm"
                leftIcon={action.icon}
                onClick={(e) => handleBulkAction(action)}
                disabled={
                  action.requiresSelection && (selectedItems?.length || 0) === 0
                }
              >
                {action.label}
              </GlassButton>
            ))}
          </HStack>

          <HStack space="sm" align="center">
            {renderViewModeToggle()}

            {(filters?.length || 0) > 0 && (
              <IconButton
                icon="🔍"
                variant={showFilters ? "primary" : "ghost"}
                size="sm"
                onClick={(e) => setShowFilters(!showFilters)}
                aria-label="Toggle filters"
              />
            )}

            {primaryAction && (
              <GlassButton
                variant="default"
                size="sm"
                leftIcon={primaryAction.icon}
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </GlassButton>
            )}
          </HStack>
        </HStack>
      );
    };

    // Render grid view
    const renderGridView = () => (
      <GlassGrid cols={12} gap="md">
        {currentPageData.map((item, index) => (
          <GlassGridItem key={item?.id} colSpan={4}>
            {renderCard ? (
              renderCard(item, index)
            ) : (
              <GlassCard
                variant="default"
                className="glass-h-full glass-cursor-pointer hover:glass-shadow-lg glass-transition-shadow"
                onClick={(e) => onRowClick?.(item)}
              >
                <VStack space="sm">
                  <div className="glass-text-sm glass-font-medium glass-text-primary">
                    {toDisplayText(
                      item?.name,
                      toDisplayText(item?.title, item?.id)
                    )}
                  </div>
                  <div className="glass-text-xs glass-text-secondary">
                    {toDisplayText(item?.description, "No description")}
                  </div>
                </VStack>
              </GlassCard>
            )}
          </GlassGridItem>
        ))}
      </GlassGrid>
    );

    // Render list view
    const renderListView = () => (
      <VStack space="sm">
        {currentPageData.map((item, index) => (
          <div
            key={item?.id}
            className="glass-animate-fade-in-up"
            style={{
              animationDelay: `${Math.min(index, 10) * 50}ms`,
              animationFillMode: "both",
            }}
          >
            {renderCard ? (
              renderCard(item, index)
            ) : (
              <GlassCard
                variant="outlined"
                className="glass-foundation-complete glass-backdrop-blur-md glass-bg-transparent glass-border-white/40 glass-shadow-2xl glass-cursor-pointer hover:glass-shadow-2xl glass-hover-scale-1-01 glass-transition-all glass-focus glass-touch-target glass-contrast-guard"
                onClick={(e) => onRowClick?.(item)}
              >
                <HStack space="md" align="center">
                  {selectable && (
                    <GlassInput
                      type="checkbox"
                      checked={selectedItems.includes(item?.id)}
                      onChange={(e) => {
                        const newSelection = e.target.checked
                          ? [...selectedItems, item?.id]
                          : selectedItems.filter((id) => id !== item?.id);
                        handleSelectionChange(newSelection);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="glass-radius-md glass-border-glass-border glass-focus-ring-primary"
                    />
                  )}

                  <VStack space="xs" className="glass-flex-1">
                    <div className="glass-text-sm glass-font-medium glass-text-primary">
                      {toDisplayText(
                        item?.name,
                        toDisplayText(item?.title, item?.id)
                      )}
                    </div>
                    <div className="glass-text-xs glass-text-primary-opacity-70">
                      {toDisplayText(item?.description, "No description")}
                    </div>
                  </VStack>

                  {Boolean(item?.status) && (
                    <GlassBadge variant="outline" size="xs">
                      {toDisplayText(item?.status)}
                    </GlassBadge>
                  )}
                </HStack>
              </GlassCard>
            )}
          </div>
        ))}
      </VStack>
    );

    // Render main content
    const renderContent = () => {
      if (loading) {
        return (
          <div className="glass-flex glass-items-center glass-justify-center glass-h-64">
            <div className="glass-w-8 glass-h-8 glass-border-2 glass-border-primary glass-border-t-transparent glass-radius-full glass-animate-spin" />
          </div>
        );
      }

      if ((currentPageData?.length || 0) === 0) {
        return (
          emptyState || (
            <div className="glass-flex glass-flex-col glass-items-center glass-justify-center glass-h-64 glass-text-center">
              <div className="glass-text-lg glass-font-medium glass-text-secondary glass-mb-2">
                No items found
              </div>
              <div className="glass-text-sm glass-text-secondary">
                Try adjusting your search or filters
              </div>
            </div>
          )
        );
      }

      switch (viewMode) {
        case "grid":
          return renderGridView();
        case "list":
          return renderListView();
        default:
          return (
            <GlassDataTable
              data={currentPageData}
              columns={columns}
              selectable={selectable}
              selectedRows={selectedItems}
              onSelectionChange={handleSelectionChange}
              onRowClick={onRowClick}
              getRowId={(row) => row.id}
              loading={loading}
              pagination={!!onPaginationChange}
              pageSizeOptions={[10, 20, 50, 100]}
              initialPageSize={pageSize}
            />
          );
      }
    };

    return (
      <div
        ref={ref}
        className={cn("w-full space-y-6", className)}
        role="region"
        aria-label={ariaLabel || title || "List View"}
        data-testid={dataTestId}
        {...props}
      >
        {/* Header */}
        <PageHeader
          title={title}
          description={description}
          actions={
            <GlassSearchInterface
              variant="compact"
              placeholder="Search..."
              value={searchQuery}
              onChange={onSearchChange}
              showFilters={false}
              showCategories={false}
              className="glass-w-64"
            />
          }
        />

        {/* Actions and filters */}
        <VStack space="md">
          {renderActionsBar()}
          {renderFilters()}
        </VStack>

        {/* Content */}
        <div>{renderContent()}</div>

        {/* Pagination */}
        {onPaginationChange && totalPages > 1 && (
          <HStack space="sm" align="center" justify="center">
            <GlassButton
              variant="ghost"
              size="sm"
              disabled={currentPage === 1}
              onClick={(e) => onPaginationChange(currentPage - 1, pageSize)}
            >
              Previous
            </GlassButton>

            <span className="glass-text-sm glass-text-secondary">
              Page {currentPage} of {totalPages}
            </span>

            <GlassButton
              variant="ghost"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={(e) => onPaginationChange(currentPage + 1, pageSize)}
            >
              Next
            </GlassButton>
          </HStack>
        )}
      </div>
    );
  }
);

GlassListView.displayName = "GlassListView";
